import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2, Building2, Users, Briefcase, Quote } from 'lucide-react';
export function Contact() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    role: '',
    interest: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  return <div className="bg-brand-paper" data-id="element-576">
      {/* Hero with warm background imagery */}
      <section className="relative pt-40 pb-24 overflow-hidden" data-id="element-577">
        {/* Background image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1542884748-2b87b36c6b90?ixlib=rb-4.0.3&auto=format&fit=crop&w=2800&q=90)'
      }} data-id="element-578"></div>
        {/* Overlays for legibility */}
        <div className="absolute inset-0 bg-brand-navy/90 mix-blend-multiply" data-id="element-579"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/60" data-id="element-580"></div>
        <div className="absolute inset-0 opacity-10" data-id="element-581">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-582"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-583">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-584">
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6" data-id="element-585">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-586">
                Contact Us
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-587">
              Let's start a{' '}
              <span className="text-brand-gold italic" data-id="element-588">conversation.</span>
            </h1>
            <p className="text-xl text-white/85 leading-relaxed font-light max-w-2xl" data-id="element-589">
              Whether you're exploring a new managed care partner, requesting a
              proposal, or just have a question — our team is ready to help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Image strip - human moments */}
      <section className="bg-white" data-id="element-590">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20" data-id="element-591">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16" data-id="element-592">
            {[{
            src: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90',
            alt: 'Family embracing'
          }, {
            src: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90',
            alt: 'Doctor with patient'
          }, {
            src: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90',
            alt: 'Worker on the job'
          }, {
            src: 'https://images.unsplash.com/photo-1609220136736-443140cffec6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=90',
            alt: 'Older couple together'
          }].map((img, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="relative aspect-square rounded-2xl overflow-hidden shadow-xl group" data-id="element-593">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-594" />
                <div className="absolute inset-0 bg-brand-navy/10 group-hover:bg-transparent transition-colors duration-500" data-id="element-595"></div>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* Contact methods */}
      <section className="py-12 bg-white" data-id="element-596">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-6" data-id="element-597">
          {[{
          icon: Phone,
          title: 'Call us',
          lines: ['855.260.1053 (Toll Free)', '714.259.1053 (Local)'],
          href: 'tel:8552601053'
        }, {
          icon: Mail,
          title: 'Email us',
          lines: ['info@arissacs.com', 'Response within one business day'],
          href: 'mailto:info@arissacs.com'
        }, {
          icon: Clock,
          title: 'Office hours',
          lines: ['Monday – Friday', '6:00 AM – 6:00 PM PST']
        }].map((card, i) => {
          const Icon = card.icon;
          const Wrapper: any = card.href ? 'a' : 'div';
          return <motion.div key={card.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} data-id="element-598">
                <Wrapper {...card.href ? {
              href: card.href
            } : {}} className="block bg-brand-paper rounded-xl p-8 hover:shadow-lg transition-shadow border border-gray-100 h-full group" data-id="element-599">
                  <div className="w-12 h-12 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-brand-navy transition-colors" data-id="element-600">
                    <Icon className="w-6 h-6" data-id="element-601" />
                  </div>
                  <h3 className="font-serif font-bold text-xl text-brand-navy mb-3" data-id="element-602">
                    {card.title}
                  </h3>
                  {card.lines.map(line => <p key={line} className="text-gray-700 text-sm leading-relaxed" data-id="element-603">
                      {line}
                    </p>)}
                </Wrapper>
              </motion.div>;
        })}
        </div>
      </section>

      {/* Form + Imagery panel */}
      <section className="py-24 bg-brand-paper" data-id="element-604">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-605">
          <div className="grid lg:grid-cols-5 gap-10" data-id="element-606">
            {/* Imagery & quote panel (left) */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.7
          }} className="lg:col-span-2 relative rounded-2xl overflow-hidden min-h-[500px] lg:min-h-full" data-id="element-607">
              <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Warm human moment representing the people we serve" className="absolute inset-0 w-full h-full object-cover" data-id="element-608" />
              <div className="absolute inset-0 bg-gradient-to-b from-brand-navy/40 via-brand-navy/70 to-brand-navy/95" data-id="element-609"></div>

              {/* Content overlay */}
              <div className="relative z-10 p-8 md:p-12 h-full flex flex-col justify-end text-white" data-id="element-610">
                <Quote className="w-10 h-10 text-brand-gold mb-6" data-id="element-611" />
                <p className="font-serif text-2xl md:text-3xl leading-snug mb-6 italic font-light" data-id="element-612">
                  "Behind every claim is a person — and a family waiting for
                  them to come home."
                </p>
                <div className="border-t border-white/20 pt-6" data-id="element-613">
                  <p className="text-sm text-white/80 leading-relaxed" data-id="element-614">
                    When you reach out to Arissa, you're not just starting a
                    vendor relationship. You're joining a team that treats every
                    injured worker like one of our own.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Form (right) */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-3" data-id="element-615">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12" data-id="element-616">
                {submitted ? <div className="text-center py-12" data-id="element-617">
                    <div className="w-20 h-20 bg-brand-gold/15 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-618">
                      <CheckCircle2 className="w-10 h-10 text-brand-gold" data-id="element-619" />
                    </div>
                    <h3 className="text-3xl font-serif text-brand-navy mb-3" data-id="element-620">
                      Thank you!
                    </h3>
                    <p className="text-gray-600 max-w-md mx-auto" data-id="element-621">
                      Your message is on its way. A member of our team will be
                      in touch within one business day.
                    </p>
                  </div> : <>
                    <h2 className="text-3xl font-serif text-brand-navy mb-2" data-id="element-622">
                      Send us a message
                    </h2>
                    <p className="text-gray-600 mb-8" data-id="element-623">
                      Tell us a little about your organization and what you're
                      looking for.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5" data-id="element-624">
                      <div className="grid sm:grid-cols-2 gap-5" data-id="element-625">
                        <div data-id="element-626">
                          <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-627">
                            First name
                          </label>
                          <input required name="firstName" value={form.firstName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-628" />
                        </div>
                        <div data-id="element-629">
                          <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-630">
                            Last name
                          </label>
                          <input required name="lastName" value={form.lastName} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-631" />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5" data-id="element-632">
                        <div data-id="element-633">
                          <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-634">
                            Work email
                          </label>
                          <input required type="email" name="email" value={form.email} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-635" />
                        </div>
                        <div data-id="element-636">
                          <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-637">
                            Phone
                          </label>
                          <input type="tel" name="phone" value={form.phone} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-638" />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5" data-id="element-639">
                        <div data-id="element-640">
                          <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-641">
                            Organization
                          </label>
                          <input required name="organization" value={form.organization} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition" data-id="element-642" />
                        </div>
                        <div data-id="element-643">
                          <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-644">
                            Your role
                          </label>
                          <select name="role" value={form.role} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition bg-white" data-id="element-645">
                            <option value="" data-id="element-646">Select a role...</option>
                            <option data-id="element-647">Insurance Carrier</option>
                            <option data-id="element-648">TPA</option>
                            <option data-id="element-649">Employer / Risk Manager</option>
                            <option data-id="element-650">Broker / Consultant</option>
                            <option data-id="element-651">Other</option>
                          </select>
                        </div>
                      </div>

                      <div data-id="element-652">
                        <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-653">
                          I'm interested in
                        </label>
                        <select name="interest" value={form.interest} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition bg-white" data-id="element-654">
                          <option value="" data-id="element-655">Select an option...</option>
                          <option data-id="element-656">A+ Ancillary Services</option>
                          <option data-id="element-657">Workers' Comp Managed Care</option>
                          <option data-id="element-658">Medical Bill Review</option>
                          <option data-id="element-659">Case Management</option>
                          <option data-id="element-660">Utilization Review</option>
                          <option data-id="element-661">Medicare Set-Aside (MSA)</option>
                          <option data-id="element-662">General inquiry</option>
                        </select>
                      </div>

                      <div data-id="element-663">
                        <label className="block text-sm font-semibold text-brand-navy mb-2" data-id="element-664">
                          Tell us more
                        </label>
                        <textarea name="message" rows={5} value={form.message} onChange={handleChange} placeholder="A few details about what you're looking for..." className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-gold focus:ring-2 focus:ring-brand-gold/20 focus:outline-none transition resize-none" data-id="element-665"></textarea>
                      </div>

                      <label className="flex items-start gap-3 pt-2 cursor-pointer group" data-id="element-666">
                        <input required type="checkbox" name="privacyAgreed" className="mt-0.5 w-4 h-4 rounded border-gray-300 text-brand-gold focus:ring-2 focus:ring-brand-gold/30 cursor-pointer flex-shrink-0" data-id="element-667" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-668">
                          By submitting this form, you agree to our{' '}
                          <Link to="/privacy" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-669">
                            privacy policy
                          </Link>
                          . We never share your information.
                        </span>
                      </label>

                      <button type="submit" className="bg-brand-gold text-brand-navy px-8 py-4 rounded-lg font-semibold hover:bg-brand-navy hover:text-white transition-colors flex items-center justify-center w-full sm:w-auto" data-id="element-670">
                        Send message <Send className="w-4 h-4 ml-2" data-id="element-671" />
                      </button>
                    </form>
                  </>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* HQ + Who We Work With with imagery */}
      <section className="py-24 bg-white" data-id="element-672">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-stretch" data-id="element-673">
          {/* HQ card with image */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-brand-navy rounded-2xl overflow-hidden relative shadow-xl" data-id="element-674">
            <div className="relative h-64" data-id="element-675">
              <img src="https://images.unsplash.com/photo-1582719471384-894fbb16e074?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Caring team working together" className="w-full h-full object-cover" data-id="element-676" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-navy to-transparent" data-id="element-677"></div>
            </div>
            <div className="p-8 md:p-10 -mt-12 relative z-10" data-id="element-678">
              <h3 className="font-serif text-3xl text-white mb-6" data-id="element-679">
                Visit our headquarters
              </h3>
              <div className="space-y-5" data-id="element-680">
                <div className="flex items-start" data-id="element-681">
                  <MapPin className="w-5 h-5 text-brand-gold mr-3 mt-1 flex-shrink-0" data-id="element-682" />
                  <div className="text-white/85 text-sm leading-relaxed" data-id="element-683">
                    Corporate Headquarters
                    <br data-id="element-684" />
                    123 Managed Care Way, Suite 100
                    <br data-id="element-685" />
                    Irvine, CA 92614
                  </div>
                </div>
                <div className="flex items-start" data-id="element-686">
                  <Phone className="w-5 h-5 text-brand-gold mr-3 mt-1 flex-shrink-0" data-id="element-687" />
                  <div className="text-sm" data-id="element-688">
                    <a href="tel:8552601053" className="block text-white/90 hover:text-brand-gold transition-colors" data-id="element-689">
                      855.260.1053
                    </a>
                    <a href="tel:7142591053" className="block text-white/90 hover:text-brand-gold transition-colors" data-id="element-690">
                      714.259.1053
                    </a>
                  </div>
                </div>
                <div className="flex items-start" data-id="element-691">
                  <Mail className="w-5 h-5 text-brand-gold mr-3 mt-1 flex-shrink-0" data-id="element-692" />
                  <a href="mailto:info@arissacs.com" className="text-sm text-white/90 hover:text-brand-gold transition-colors" data-id="element-693">
                    info@arissacs.com
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Who we work with with imagery */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="bg-brand-paper rounded-2xl overflow-hidden relative shadow-sm border border-gray-100" data-id="element-694">
            <div className="relative h-64" data-id="element-695">
              <img src="https://images.unsplash.com/photo-1607582544086-8bcefa37ae89?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Medical professional providing care" className="w-full h-full object-cover" data-id="element-696" />
              <div className="absolute inset-0 bg-gradient-to-t from-brand-paper to-transparent" data-id="element-697"></div>
            </div>
            <div className="p-8 md:p-10 -mt-12 relative z-10" data-id="element-698">
              <h3 className="font-serif text-3xl text-brand-navy mb-6" data-id="element-699">
                Who we work with
              </h3>
              <ul className="space-y-4" data-id="element-700">
                <li className="flex items-start bg-white p-4 rounded-lg shadow-sm" data-id="element-701">
                  <div className="w-10 h-10 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mr-4 flex-shrink-0" data-id="element-702">
                    <Building2 className="w-5 h-5" data-id="element-703" />
                  </div>
                  <div data-id="element-704">
                    <div className="font-semibold text-brand-navy" data-id="element-705">
                      Insurance Carriers
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-706">
                      Comprehensive managed care for national & regional
                      carriers.
                    </div>
                  </div>
                </li>
                <li className="flex items-start bg-white p-4 rounded-lg shadow-sm" data-id="element-707">
                  <div className="w-10 h-10 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mr-4 flex-shrink-0" data-id="element-708">
                    <Briefcase className="w-5 h-5" data-id="element-709" />
                  </div>
                  <div data-id="element-710">
                    <div className="font-semibold text-brand-navy" data-id="element-711">
                      Third Party Administrators
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-712">
                      White-label and co-branded solutions for TPAs of every
                      size.
                    </div>
                  </div>
                </li>
                <li className="flex items-start bg-white p-4 rounded-lg shadow-sm" data-id="element-713">
                  <div className="w-10 h-10 rounded-lg bg-brand-navy text-brand-gold flex items-center justify-center mr-4 flex-shrink-0" data-id="element-714">
                    <Users className="w-5 h-5" data-id="element-715" />
                  </div>
                  <div data-id="element-716">
                    <div className="font-semibold text-brand-navy" data-id="element-717">
                      Public & Private Employers
                    </div>
                    <div className="text-sm text-gray-600" data-id="element-718">
                      Self-insured employers across every major industry.
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}